<template>
    <div class="campaign-header__nav">
        <div v-if="clientLogo" class="campaign-header__logo">
            <router-link :to="clientLink">
                <img :src="clientLogo" class="campaign-header__logo-image" />
            </router-link>
        </div>

        <Header class="campaign-header hoxton-header">
            <Breadcrumb separator="/" class="campaign-header__breadcrumbs">
                <breadcrumb-item
                    v-for="breadcrumb in breadcrumbs"
                    :key="breadcrumb.label"
                    class="campaign-header__breadcrumb"
                >
                    <template v-if="!breadcrumb.path">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span class="inactive-link" v-html="breadcrumb.label"></span>
                    </template>
                    <router-link v-else :to="breadcrumb.path">
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <span v-html="breadcrumb.label"></span>
                    </router-link>
                </breadcrumb-item>
            </Breadcrumb>
        </Header>

        <div class="hoxton-footer">
            <footer-nav theme="light" />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import type { Client } from "shared-types";
import { type UserActions } from "shared-utils/enums/user";

import FooterNav from "@/FooterNav.vue";
import clientsQuery from "@/apollo/queries/Clients.gql";
import bus from "@/bus";
import { UiAction } from "@/store/modules/ui";
import { AppRouteNames } from "@/router/constants";
import { userCan } from "./services/Auth";

export default defineComponent({
    name: "CampaignHeader",

    components: {
        FooterNav
    },

    data() {
        return {
            clients: [] as Client[]
        };
    },

    computed: {
        breadcrumbs() {
            return this.$route.matched
                .map(({ meta: _meta, path }) => {
                    const meta = _meta as {
                        breadcrumb: string;
                        permission?: {
                            action?: UserActions;
                            scope?: { clientId?: string; campaignId?: string };
                        };
                    };

                    let label = meta.breadcrumb;
                    let link: string | null = path;

                    if (label === ":client") {
                        if (this.client._id !== this.clientId) {
                            label = "Client";
                        } else {
                            label = `Client: <strong>${this.client.name}</strong>`;
                        }
                    } else if (label === ":campaign") {
                        if (this.campaign._id !== this.campaignId) {
                            label = "Campaign";
                        } else {
                            label = `Campaign: <strong>${this.campaign.name}</strong>`;
                        }
                    }

                    link = link.replace(":clientId", this.clientId);
                    link = link.replace(":campaignId", this.campaignId);

                    if (meta.permission?.action) {
                        link = userCan(meta.permission.action, {
                            ...(meta.permission.scope?.clientId && { clientId: this.clientId }),
                            ...(meta.permission?.scope?.campaignId && { campaignId: this.campaignId })
                        })
                            ? link
                            : null;
                    }

                    return {
                        path: link,
                        label
                    };
                })
                .filter(breadcrumb => breadcrumb.label);
        },
        campaign() {
            return this.$store.state.campaign;
        },
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        client() {
            return this.$store.state.campaign.client;
        },
        clientId() {
            return "clientId" in this.$route.params ? (this.$route.params.clientId as string) : "";
        },
        clientLink() {
            return {
                name: AppRouteNames.CLIENT_CAMPAIGNS,
                params: { clientId: this.clientId }
            };
        },
        clientLogo() {
            return this.$store.state.ui.currentClient?.logoUrl;
        }
    },
    methods: {
        setCurrentClient(client: Client) {
            void this.$store.dispatch(UiAction.SetCurrentClient, client);
        }
    },
    apollo: {
        clients: {
            query: clientsQuery,
            variables() {
                return {
                    orderBy: "name_ASC",
                    wppProjectId: this.$store.state.auth?.wppOsContext?.project?.id
                };
            },
            error(e) {
                bus.$emit("apolloErrorEvent", e as Error);
            },
            result({ data }: { data: { clients: Client[] } }) {
                if (!this.clientId) {
                    return;
                }

                if (typeof data.clients === "undefined") {
                    void this.$router.push({ name: "UserNotFound" });
                }

                // if user can't do anything in the client screen lets redirect it to the campaign dashboard
                const currentClient = data.clients.find(client => client._id === this.clientId);
                if (!currentClient && this.$apollo.queries.navigation) {
                    void this.$apollo.queries.navigation.refetch();
                    return;
                }

                this.clients = data.clients.map(client => {
                    if (client._id === this.clientId) {
                        this.setCurrentClient(client);
                    }

                    return {
                        ...client,
                        content: client.name
                    };
                });
            }
        }
    }
});
</script>

<style lang="css" scoped>
.campaign-header__logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    margin-right: 20px;

    a {
        margin: auto 0;
    }
}

.campaign-header__logo-image {
    height: 36px;
    max-height: 36px;
}
</style>
